import { forwardRef } from "react";

import { Box } from "@mui/material";

import { getFitModeMetrics, getScaledImageMetrics, type AspectRatio, type DragOffset } from "./helpers";

import { type FitImageResult } from "@/components/ImageUploader/utils";

export interface EditorCanvasProps {
  imageDataUrl: string;
  fittedImage: FitImageResult;
  aspectRatio: AspectRatio | null;
  dragOffset: DragOffset;
  isFitMode: boolean;
  targetSize: number;
  viewportSize: number;
  useWhiteBackground?: boolean;
  onImageLoad?: () => void;
}

export const EditorCanvas = forwardRef<HTMLImageElement, EditorCanvasProps>(
  (
    {
      imageDataUrl,
      fittedImage,
      aspectRatio,
      dragOffset,
      isFitMode,
      targetSize,
      viewportSize,
      onImageLoad,
      useWhiteBackground = false,
    },
    ref
  ) => {
    const metrics = isFitMode
      ? getFitModeMetrics(fittedImage, viewportSize, targetSize, aspectRatio)
      : getScaledImageMetrics(fittedImage, dragOffset, viewportSize, targetSize);

    const { width: scaledWidth, height: scaledHeight, offsetX: scaledOffsetX, offsetY: scaledOffsetY } = metrics;

    return (
      <>
        {/* Background */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            ...(useWhiteBackground
              ? { backgroundColor: "white" }
              : {
                  backgroundImage: `url(${imageDataUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "blur(20px)",
                }),
            zIndex: 0,
          }}
        />

        {/* Main image */}
        <Box
          component="img"
          ref={ref}
          src={isFitMode ? imageDataUrl : fittedImage.dataUrl}
          alt="Editor image"
          onLoad={onImageLoad}
          sx={{
            position: "absolute",
            width: `${scaledWidth}px`,
            height: `${scaledHeight}px`,
            transform: `translate(${scaledOffsetX}px, ${scaledOffsetY}px)`,
            userSelect: "none",
            zIndex: 1,
            pointerEvents: "none",
            maxWidth: "none",
            maxHeight: "none",
          }}
          draggable={false}
        />
      </>
    );
  }
);

EditorCanvas.displayName = "EditorCanvas";
